import * as React from 'react'
import styled, { css } from 'styled-components'

import { t } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

type FrequencySwitchProps = {
  className?: string | undefined
  frequency: models.project.DonationFrequency
  setFrequency: React.Dispatch<React.SetStateAction<models.project.DonationFrequency>>
}

export function FrequencySwitch({ className, frequency, setFrequency }: FrequencySwitchProps): React.ReactElement<any> {
  const leftLabel = t('One-time')
  const rightLabel = t('Monthly')

  const { containerWidth, leftOffset, pillWidth } = React.useMemo(() => {
    const containerWidth = (leftLabel.length + rightLabel.length) * 10 + 10
    const pillWidth = containerWidth / 2
    const leftOffset = (leftLabel.length + rightLabel.length) * 5

    return {
      containerWidth,
      leftOffset,
      pillWidth,
    }
  }, [])

  return (
    <Wrapper className={className} data-testid="component:tip.frequency-switch">
      <Container $width={containerWidth}>
        <Checkbox
          checked={frequency === 'monthly'}
          $leftOffset={leftOffset}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFrequency(event.target.checked ? 'monthly' : 'one-time')
          }}
        />
        <Pill $secondLabel={rightLabel} $width={pillWidth}>
          <Label>{leftLabel}</Label>
        </Pill>
        <Structure></Structure>
      </Container>
    </Wrapper>
  )
}

export const TipFrequencySwitch = styled(FrequencySwitch)`
  margin: 16px auto;
  height: fit-content;
`

const Container = styled.div<{ $width: number }>`
  margin: -20px auto 0 auto;
  position: relative;
  height: 26px;

  ${({ $width }) => {
    if ($width) {
      return css`
        width: ${$width}px;
      `
    }
  }};
`

const Label = styled(plume.styles.copy.S.regular)`
  color: ${plume.COLORS.PRIMARY_GREY_000};
  display: inline-block;
  z-index: 1;
`

const Pill = styled.div<{ $secondLabel: string; $width: number }>`
  z-index: 2;

  &::after,
  &::before,
  ${Label} {
    border-radius: 2px;
    height: 8px;
    line-height: 0.4;
    padding: 9px 4px;
    position: absolute;
    text-align: center;
    transition: 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) all;

    ${({ $width }) => {
      if ($width) {
        return css`
          width: ${$width}px;
        `
      }
    }};
  }

  ${({ $secondLabel }) => {
    if ($secondLabel) {
      return css`
        &::after {
          ${plume.TYPOGRAPHY.COPY_S_REGULAR};
          color: ${plume.COLORS.TEXT_TERTIARY};
          content: '${$secondLabel}';
          line-height: 24px;
          padding: 0;
          right: 0;
        }
      `
    }
  }};

  &::before {
    background-color: ${plume.COLORS.PRIMARY_BLUE_500};
    border-radius: 24px;
    content: '';
    left: 0;
  }
`

const Checkbox = styled.input.attrs({ type: 'checkbox' })<{ $leftOffset: number }>`
  cursor: pointer;
  height: 100%;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: relative;
  width: 100%;
  z-index: 3;

  // FIXME: Replace 'div' with named element?
  &:checked + div > ${plume.styles.copy.S.regular} {
    color: ${plume.COLORS.TEXT_TERTIARY};
  }

  &:checked + ${Pill}::after {
    color: ${plume.COLORS.PRIMARY_GREY_000};
  }

  &:checked + ${Pill}::before {
    background-color: ${plume.COLORS.PRIMARY_BLUE_500};
    border-radius: 24px;

    ${({ $leftOffset }) => {
      if ($leftOffset) {
        return css`
          left: ${$leftOffset}px;
        `
      }
    }};
  }
`

const Structure = styled.div`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 24px;
  border: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
  box-sizing: border-box;
  transition: 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) all;
  width: 100%;
  z-index: 1;
`

const Wrapper = styled.div`
  ${Structure}, ${Pill} {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`
